import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Filter, X } from 'lucide-react'
import { AutocompleteInput } from '../../../shared/components/ui/AutocompleteInput'
import { useApi } from '../../../shared/hooks/useApi'

interface ContractorsFiltersProps {
  onFiltersChange: (filters: { year?: number; cpvCode: string; contractorName: string }) => void
}

export default function ContractorsFilters({ onFiltersChange }: ContractorsFiltersProps) {
  const { getContractorSuggestions, getCPVSuggestions } = useApi()
  const [searchParams, setSearchParams] = useSearchParams()

  // Initialize filters from URL
  const [filters, setFilters] = useState(() => {
    const yearParam = searchParams.get('year')
    return {
      year: yearParam ? parseInt(yearParam) : undefined,
      cpvCode: searchParams.get('cpv_code') || '',
      contractorName: searchParams.get('contractor_name') || '',
    }
  })

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.year) params.set('year', filters.year.toString())
    if (filters.cpvCode) params.set('cpv_code', filters.cpvCode)
    if (filters.contractorName) params.set('contractor_name', filters.contractorName)

    setSearchParams(params, { replace: true })

    // Notify parent component
    onFiltersChange(filters)
  }, [filters, setSearchParams, onFiltersChange])

  const handleInputChange = (field: keyof typeof filters, value: string | number | undefined) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    const emptyFilters = {
      year: undefined,
      cpvCode: '',
      contractorName: '',
    }
    setFilters(emptyFilters)
  }

  const hasActiveFilters = () => {
    return filters.cpvCode.trim() !== '' || filters.contractorName.trim() !== ''
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i)

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-ink-muted" />
          <h3 className="text-lg font-medium text-ink">Filtry wykonawców</h3>
          {hasActiveFilters() && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
              Aktywne filtry
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Year Selector */}
        <div>
          <label htmlFor="year" className="block text-sm font-medium text-ink-secondary mb-1">
            Rok
          </label>
          <select
            id="year"
            value={filters.year || ''}
            onChange={(e) =>
              handleInputChange('year', e.target.value ? parseInt(e.target.value) : undefined)
            }
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
          >
            <option value="">Wszystkie lata</option>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* CPV Code - with autocomplete */}
        <div>
          <label htmlFor="cpv-code" className="block text-sm font-medium text-ink-secondary mb-1">
            Kod CPV
          </label>
          <AutocompleteInput
            id="cpv-code"
            value={filters.cpvCode}
            onChange={(value: string) => handleInputChange('cpvCode', value)}
            onSuggestionsFetch={getCPVSuggestions}
            placeholder="np. 45000000"
          />
        </div>

        {/* Contractor Name - with autocomplete */}
        <div>
          <label htmlFor="contractor-name" className="block text-sm font-medium text-ink-secondary mb-1">
            Nazwa wykonawcy
          </label>
          <AutocompleteInput
            id="contractor-name"
            value={filters.contractorName}
            onChange={(value: string) => handleInputChange('contractorName', value)}
            onSuggestionsFetch={getContractorSuggestions}
            placeholder="Wpisz nazwę wykonawcy..."
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center space-x-4">
          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-inset text-ink hover:bg-surface-inset"
            >
              <X className="h-4 w-4 mr-1" />
              Wyczyść filtry
            </button>
          )}
        </div>

        <div className="text-sm text-ink-muted">
          {hasActiveFilters() ? 'Filtry aktywne' : 'Brak aktywnych filtrów'}
        </div>
      </div>

      {/* Filter summary */}
      {hasActiveFilters() && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {filters.cpvCode && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai">
                CPV: {filters.cpvCode}
              </span>
            )}
            {filters.contractorName && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                Wykonawca: {filters.contractorName}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
